import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useDragControls, type PanInfo } from 'framer-motion';
import { cn } from '../../lib/cn';
import { getOverlayRoot, lockAppChrome } from '../../lib/overlayRoot';
import { EASE_OUT_IOS, SHEET_DISMISS, SPRING_POP, SPRING_SHEET, prefersReducedMotion, useIsDesktop } from '../../lib/motion';

/** Drag distance / fling velocity past which a downward drag dismisses the sheet. */
const DISMISS_DISTANCE = 110;
const DISMISS_VELOCITY = 700;

/**
 * The one true bottom sheet. A single, physics-driven surface every modal in the
 * app shares so they all open, settle and dismiss identically:
 *
 *  - backdrop fades in / out (never a hard cut),
 *  - the panel springs up from the bottom and animates back down on close
 *    (real exit animation, via AnimatePresence),
 *  - grab the handle to drag it down — past a threshold (or with a flick) it
 *    dismisses, otherwise it springs back,
 *  - portaled to `#overlay-root` so it's never clipped, dragged along by an
 *    ancestor's transform, or painted under the tab bar,
 *  - Escape closes it; honours Reduce Motion (fades instead of sliding).
 *
 * Callers own the inner layout (header / body / footer); the handle is built in.
 */
export function Sheet({
  open,
  onClose,
  children,
  ariaLabel,
  className,
  showHandle = true,
  mobileOnly = false,
  tone = 'light',
  desktopVariant = 'dialog'
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  /** Extra classes for the panel (e.g. `max-h-[82vh] flex flex-col`). */
  className?: string;
  showHandle?: boolean;
  /** Hide on desktop (callers that use a dropdown there instead). */
  mobileOnly?: boolean;
  /** Panel chrome — `dark` for immersive night screens (e.g. Make Your Picks). */
  tone?: 'light' | 'dark';
  /**
   * How this surface behaves on tablet + desktop (`md`, 768px+). Phones always get
   * the bottom sheet, untouched.
   *   - `dialog` (default) — a centred, scale-in modal (the right idiom for a menu,
   *     info panel or confirmation on a pointer device).
   *   - `sheet` — keep the bottom sheet everywhere (drag-to-dismiss pickers).
   *   - `hidden` — render nothing on desktop (the caller shows its own dropdown/popover).
   * Ignored when `mobileOnly` is set (that keeps its own `md:hidden` + dropdown path).
   */
  desktopVariant?: 'dialog' | 'sheet' | 'hidden';
}) {
  const controls = useDragControls();
  const reduce = prefersReducedMotion();
  const wide = useIsDesktop();

  useEffect(() => {
    if (!open) return;
    const unlock = lockAppChrome();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      unlock();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  const onDragEnd = (_e: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_DISTANCE || info.velocity.y > DISMISS_VELOCITY) onClose();
  };

  // Tablet + desktop overrides. Phones fall through to the bottom sheet below.
  if (wide && !mobileOnly && desktopVariant === 'hidden') return null;
  if (wide && !mobileOnly && desktopVariant === 'dialog') {
    return createPortal(
      <AnimatePresence>
        {open && (
          <div className="pointer-events-auto fixed inset-0 flex items-center justify-center p-6">
            <motion.div
              aria-hidden
              onClick={onClose}
              className={cn(
                'absolute inset-0 backdrop-blur-[3px]',
                tone === 'dark' ? 'bg-black/55' : 'bg-night/45'
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: EASE_OUT_IOS }}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              className={cn(
                'relative w-full max-w-md max-h-[85vh] overflow-y-auto overscroll-contain rounded-[28px]',
                tone === 'dark'
                  ? 'bg-[linear-gradient(180deg,#101016,#0A0A0E)] shadow-[0_30px_70px_-24px_rgba(0,0,0,0.8)] ring-1 ring-white/[0.08]'
                  : 'bg-surface-top shadow-[0_30px_70px_-24px_rgba(10,10,40,0.55)] ring-1 ring-black/[0.05]',
                className
              )}
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 8 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 6 }}
              transition={reduce ? { duration: 0.16, ease: EASE_OUT_IOS } : SPRING_POP}
            >
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
      getOverlayRoot()
    );
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="sheet-backdrop"
            aria-hidden
            onClick={onClose}
            className={cn(
              'pointer-events-auto fixed inset-0 backdrop-blur-[3px]',
              tone === 'dark' ? 'bg-black/55' : 'bg-night/45',
              mobileOnly && 'md:hidden'
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT_IOS }}
          />
          <motion.div
            key="sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className={cn(
              'pointer-events-auto fixed inset-x-0 bottom-0 mx-auto w-full max-w-app rounded-t-[28px] pb-safe md:bottom-10 md:max-w-md md:rounded-[28px]',
              tone === 'dark'
                ? 'bg-[linear-gradient(180deg,#101016,#0A0A0E)] shadow-[0_-24px_60px_-22px_rgba(0,0,0,0.75)] ring-1 ring-white/[0.08]'
                : 'bg-surface-top shadow-[0_-24px_60px_-22px_rgba(10,10,40,0.55)] ring-1 ring-black/[0.04]',
              mobileOnly && 'md:hidden',
              className
            )}
            initial={reduce ? { opacity: 0 } : { y: '100%' }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            // Spring up on present, but leave on a bounded tween — a full-height
            // spring has a long tail that lingers at the bottom edge before unmount.
            exit={
              reduce
                ? { opacity: 0, transition: { duration: 0.18, ease: EASE_OUT_IOS } }
                : { y: '100%', transition: SHEET_DISMISS }
            }
            transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_SHEET}
            drag={reduce ? false : 'y'}
            dragControls={controls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={onDragEnd}
          >
            {showHandle && (
              <div
                className="flex cursor-grab touch-none justify-center pb-1 pt-3 active:cursor-grabbing"
                onPointerDown={reduce ? undefined : (e) => controls.start(e)}
              >
                <div className={cn('h-[5px] w-[44px] rounded-full', tone === 'dark' ? 'bg-white/20' : 'bg-black/15')} />
              </div>
            )}
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}

/**
 * Centered alert / confirmation dialog — the same backdrop fade as {@link Sheet}
 * but the panel scales up into place (and back out) instead of sliding, the
 * right idiom for a short, focused decision (e.g. "Delete account?").
 */
export function Modal({
  open,
  onClose,
  children,
  ariaLabel,
  className
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  className?: string;
}) {
  const reduce = prefersReducedMotion();

  useEffect(() => {
    if (!open) return;
    const unlock = lockAppChrome();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      unlock();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="pointer-events-auto fixed inset-0 flex items-center justify-center p-6">
          <motion.div
            aria-hidden
            onClick={onClose}
            className="absolute inset-0 bg-night/50 backdrop-blur-[3px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT_IOS }}
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className={cn(
              'relative w-full max-w-[340px] rounded-[24px] bg-card p-6 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]',
              className
            )}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 8 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 6 }}
            transition={reduce ? { duration: 0.16, ease: EASE_OUT_IOS } : SPRING_POP}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}
